import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Step from '@mui/material/Step';
import { connect } from 'react-redux';
import Modal from '@mui/material/Modal';
import { useDispatch } from 'react-redux';
import Divider from '@mui/material/Divider';
import { compose } from '@reduxjs/toolkit';
import { useParams } from 'react-router-dom';
import Stepper from '@mui/material/Stepper';
import { useNavigate } from 'react-router-dom';
import StepLabel from '@mui/material/StepLabel';
import Typography from '@mui/material/Typography';

import Spacer from 'core/spacer/Spacer';
import Button from 'core/buttons/Button';
import CircularProgress from 'core/progress/CircularProgress';

import { buildRoute } from 'routes';
import ucfirst from 'helpers/ucfirst';
import isEmpty from 'helpers/isEmpty';
import {
  retryToCreate,
  retryToUpdate,
  setShowEditProcessingModal,
  setShowCreateProcessingModal,
} from 'app/features/api-configs/apiConfigSlice';

const style = {
  position: 'absolute',
  top: '64px',
  right: '0',
  width: 384,
  height: 'calc(100vh - 64px)',
  bgcolor: 'background.paper',
  border: 0,
  boxShadow: 24,
  p: 4,
};

function getStep1Label(isAddMode) {
  return isAddMode ? 'Creating New ApiConfig' : 'Updating apiConfig Details';
}

function getStep2Label(isAddMode, isProcessed, isFailed) {
  if (isAddMode) {
    return isProcessed
      ? 'ApiConfig Created successfully'
      : isFailed
      ? 'Failed to create apiConfig'
      : '';
  }

  return isProcessed
    ? 'ApiConfig Updated successfully'
    : isFailed
    ? 'Failed to update apiConfig'
    : '';
}

function ApiConfigProgressIndicatorModal(props) {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const params = useParams();

  const { isAddMode, open, isProcessing, isProcessed, isFailed, apiConfig, errorMessage } = props;

  const steps = useMemo(() => {
    return [
      {
        label: getStep1Label(isAddMode),
        showProgress: true,
        color: 'primary',
      },
      {
        label: getStep2Label(isAddMode, isProcessed, isFailed),
        showProgress: false,
        color: isProcessed ? 'primary' : 'error',
      },
    ];
  }, [isAddMode, isProcessed, isFailed]);

  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const hideProgressModal = () => {
    dispatch(isAddMode ? setShowCreateProcessingModal(false) : setShowEditProcessingModal(false));
  };

  useEffect(() => {
    if (isProcessing) handleNext();
  }, [isProcessing]);

  useEffect(() => {
    if (isProcessed) handleNext();
  }, [isProcessed]);

  return (
    <div>
      <Modal open={open} onClose={() => {}}>
        <Box sx={style}>
          <Box sx={{ maxWidth: 400 }}>
            {/* Step process */}

            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step) => (
                <Step key={step.label}>
                  <StepLabel color={step.color}>
                    <Box display="flex" gap={2} alignItems="center">
                      <Typography color={step.color}>{step.label}</Typography>
                      {isProcessing && step.showProgress && (
                        <CircularProgress size={30} thickness={6} />
                      )}
                    </Box>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            <Spacer />

            {/* If Form Process Failed */}

            {isFailed && (
              <Box>
                <Typography variant="body2" color="error">
                  {ucfirst(errorMessage)}
                </Typography>
                <Spacer />
                <Button
                  color="primary"
                  onClick={() => dispatch(isAddMode ? retryToCreate() : retryToUpdate())}
                  fullWidth
                >
                  Retry
                </Button>
                <Spacer />
                <Button
                  color="error"
                  onClick={() => hideProgressModal()}
                  variant="outlined"
                  fullWidth
                >
                  Cancel
                </Button>
              </Box>
            )}

            {/* Is Form Process succeed */}
            {apiConfig && (
              <Box display="flex" flexDirection="column" gap={1}>
                <Box>
                  <Button
                    color="primary"
                    onClick={() => {
                      hideProgressModal();
                      navigate(buildRoute('api-configs.index'));
                    }}
                    fullWidth
                  >
                    API Config List
                  </Button>
                </Box>
                <Box>
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={() =>
                      navigate(
                        buildRoute('api-configs.show', apiConfig.config_id ?? params.apiConfigId),
                      )
                    }
                    fullWidth
                  >
                    View Details
                  </Button>
                </Box>
                {isEmpty(params?.apiConfigId) && (
                  <Button
                    color="primary"
                    onClick={() => hideProgressModal()}
                    variant="outlined"
                    fullWidth
                  >
                    Add New ApiConfig
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </div>
  );
}

export default compose(connect(mapStateToProps))(ApiConfigProgressIndicatorModal);

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    open: ownProps.isAddMode
      ? state.apiConfigs.create.showProcessingModal
      : state.apiConfigs.update.showProcessingModal,
    isProcessing: ownProps.isAddMode
      ? state.apiConfigs.create.isCreating
      : state.apiConfigs.update.isUpdating,
    isProcessed: ownProps.isAddMode
      ? state.apiConfigs.create.isCreated
      : state.apiConfigs.update.isUpdated,
    isFailed: ownProps.isAddMode
      ? state.apiConfigs.create.isFailed
      : state.apiConfigs.update.isFailed,
    apiConfig: ownProps.isAddMode ? state.apiConfigs.create.data : state.apiConfigs.update.data,
    errorMessage: ownProps.isAddMode
      ? state.apiConfigs.create.errorMessage
      : state.apiConfigs.update.errorMessage,
  };
}

ApiConfigProgressIndicatorModal.propTypes = {
  isAddMode: PropTypes.bool.isRequired,
  open: PropTypes.bool,
  isProcessing: PropTypes.bool,
  isProcessed: PropTypes.bool,
  apiConfig: PropTypes.object,
  isFailed: PropTypes.bool,
};
